import React, { useEffect, useRef } from 'react';
import PaymentForm from './PaymentForm';
import PaymentView from './PaymentView';
import LoadingComponent from './LoadingComponent';
import ErrorComponent from './ErrorComponent';
import { usePaymentData } from '../hooks/usePaymentData';
import { usePaymentPopup } from '../hooks/usePaymentPopup';
import { usePaymentProcess } from '../hooks/usePaymentProcess';
import { useLanguage } from '../i18n/LanguageProvider';

const PaymentPortal: React.FC = () => {
  const initialized = useRef(false);
  const { t } = useLanguage();
  
  const {
    paymentData,
    loading,
    error,
    currentToken,
    setCurrentToken,
    fetchPaymentInfo,
    setError,
    setLoading
  } = usePaymentData();

  const {
    showPopup,
    popupMessage,
    popupType,
    showSuccessPopup,
    showCancelPopup
  } = usePaymentPopup();

  const {
    paymentFormData,
    handlePaymentClick
  } = usePaymentProcess({
    currentToken,
    paymentData,
    setError,
    setLoading
  });

  useEffect(() => {
    // Provjera da se ne izvršava duplo zbog React.StrictMode
    if (initialized.current) return;
    initialized.current = true;
    
    // Prevent zoom on iOS
    document.addEventListener('touchstart', function() {}, { passive: true });
    
    // Čitanje token i result parametra iz URL-a
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    const result = urlParams.get('result');
    
    // Čuvamo token za kasnije korišćenje
    setCurrentToken(token);
    
    // Proveravamo da li je payment sistem redirect-ovao sa result parametrom
    if (result) {
      handlePaymentResult(result, token);
    } else {
      fetchPaymentInfo(token);
    }
  }, []);

  const handlePaymentResult = async (result: string, token: string | null) => {

    // Prvo učitavamo podatke sa backend-a
    await fetchPaymentInfo(token);
    
    // Zatim prikazujemo popup na osnovu rezultata
    if (result === 'success') {
      showSuccessPopup(t('paymentDone'));
    } else if (result === 'cancel') {
      showCancelPopup(t('paymentFailed'));
    }
  };

  if (loading) {
    return <LoadingComponent />;
  }

  if (error) {
    const handleRetry = () => {
      fetchPaymentInfo(currentToken);
    };

    return (
      <ErrorComponent 
        error={error} 
        onRetry={handleRetry}
        showRetryButton={error !== t('missingAccess') && currentToken !== null}
      />
    );
  }

  // Ako imamo payment form podatke, prikaži auto-submit formu
  if (paymentFormData && paymentData) {
    return <PaymentForm paymentData={paymentFormData} paymentPortalUrl={paymentData.ipgAgentUrl} />;
  }

  return (
    <PaymentView
      paymentData={paymentData!}
      onPaymentClick={handlePaymentClick}
      showPopup={showPopup}
      popupMessage={popupMessage}
      popupType={popupType}
    />
  );
};

export default PaymentPortal;